import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiRequest } from '../api/client.js';

export type BusinessAccountSummary = {
  readonly id: string; readonly displayName: string; readonly status?: string; readonly health?: string;
  readonly limit?: { readonly state: string; readonly maxUniqueRecipients?: number | 'unlimited' | null };
  readonly phones?: readonly unknown[];
  readonly metaBusinessId?: string; readonly wabaId?: string; readonly lastSyncedAt?: string | null;
};

const selectionKey = 'dsh.business-account-id';
type BusinessScope = {
  readonly accounts: readonly BusinessAccountSummary[] | null;
  readonly account: BusinessAccountSummary | null;
  readonly error: boolean;
  readonly selectAccount: (id: string) => void;
  readonly refresh: () => void;
  readonly updateAccount: (account: BusinessAccountSummary) => void;
};
const Context = createContext<BusinessScope | null>(null);
const readSelection = (): string | null => {
  try { return window.sessionStorage.getItem(selectionKey); } catch { return null; }
};

// Only a non-secret account ID is persisted, and only in this browser tab.
export const BusinessScopeProvider = ({ children }: { readonly children: ReactNode }) => {
  const [accounts, setAccounts] = useState<readonly BusinessAccountSummary[] | null>(null);
  const [selectedId, setSelectedId] = useState(readSelection);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setError(false);
    void apiRequest<{ data: BusinessAccountSummary[] }>('/api/v1/business-accounts')
      .then((result) => {
        if (!Array.isArray(result?.data)) throw new Error('Invalid account response');
        if (active) setAccounts(result.data);
      })
      .catch(() => { if (active) { setAccounts(null); setError(true); } });
    return () => { active = false; };
  }, [revision]);
  const account = accounts?.find((item) => item.id === selectedId) ?? accounts?.[0] ?? null;
  const accountId = account?.id ?? null;
  useEffect(() => {
    if (accounts === null) return;
    try {
      if (accountId === null) window.sessionStorage.removeItem(selectionKey);
      else window.sessionStorage.setItem(selectionKey, accountId);
    } catch { /* Storage may be unavailable; selection still works in memory. */ }
  }, [accountId, accounts]);
  const selectAccount = useCallback((id: string) => {
    if (accounts?.some((item) => item.id === id)) setSelectedId(id);
  }, [accounts]);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const updateAccount = useCallback((updated: BusinessAccountSummary) => {
    setAccounts((current) => current?.some((item) => item.id === updated.id)
      ? current.map((item) => item.id === updated.id ? updated : item)
      : [...(current ?? []), updated]);
  }, []);
  const value = useMemo(() => ({ accounts, account, error, selectAccount, refresh, updateAccount }),
    [accounts, account, error, selectAccount, refresh, updateAccount]);
  return createElement(Context.Provider, { value }, children);
};

export const useBusinessAccounts = (): BusinessScope => {
  const scope = useContext(Context);
  if (scope === null) throw new Error('useBusinessAccounts requires BusinessScopeProvider');
  return scope;
};
